import { DestroyRef, effect, Injectable, inject, Injector } from '@angular/core';
import { UserService } from './user.service';
import { NavigatieVoorkeurService } from './navigatie-voorkeur.service';
import { isEmpty } from 'lodash-es';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtmEventName, GtmEvents } from './gtm-events';

declare global {
	interface Window {
		dataLayer: any[];
	}
}

/**
 * In principe wordt de config van het huidige "Live" environment ingeladen (tenzij je aan het previewen/debuggen bent via de Google Tag Assistant).
 * De Google Analytics events gaan dan naar de "CL Prod" GA4 property.
 *
 * Als er echter gtm_auth/gtm_preview parameters meegegeven worden, wordt de config van een Custom Environment (horend bij dezelfde GTM Container)
 * ingeladen. De events gaan dan naar de GA4 property die voor dat Environment geconfigureerd staat.
 */
@Injectable({ providedIn: 'root' })
export class GtmService {
	private readonly userService = inject(UserService);
	private readonly navigatieVoorkeurService = inject(NavigatieVoorkeurService);
	private readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);
	private readonly destroyRef = inject(DestroyRef);
	private readonly injector = inject(Injector);

	init() {
		const { gtmContainerId, gtmAuth, gtmPreview } = this.envConfig;
		if (isEmpty(gtmContainerId)) return;

		const s = document.createElement('script');
		s.type = 'text/javascript';
		s.innerHTML =
			'window.dataLayer = window.dataLayer || [];' +
			'(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push' +
			"({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)" +
			"[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=" +
			"'https://www.googletagmanager.com/gtm.js?id='+i+dl" +
			(isEmpty(gtmAuth) ? '' : `+'&gtm_auth=${gtmAuth}&gtm_preview=${gtmPreview}&gtm_cookies_win=x'`) +
			';f.parentNode.insertBefore(j,f);})' +
			`(window,document,'script','dataLayer','${gtmContainerId}');`;
		const head = document.getElementsByTagName('head')[0];
		head.appendChild(s);

		this.userService.rolChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((rol) => window.dataLayer.push({ rol }));
		this.userService.instelling$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ bron }) => window.dataLayer.push({ bron }));

		effect(
			() => {
				window.dataLayer.push({
					nieuweNavigatie: this.navigatieVoorkeurService.nieuweNavigatie(),
					navigatieKeuze: this.navigatieVoorkeurService.navigatieKeuze(),
				});
			},
			{ injector: this.injector }
		);
	}

	pushEvent<E extends GtmEventName>(event: E, ...[params]: GtmEvents[E] extends void ? [] : [GtmEvents[E]]) {
		window.dataLayer ??= [];
		window.dataLayer.push({ event, ...(params as object | undefined) });
	}
}
